import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout as AntLayout, Menu } from 'antd';
import {
  DashboardOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  FileTextOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  CarOutlined,
  InboxOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  FileAddOutlined,
  BellOutlined,
  ShopOutlined,
  CheckSquareOutlined,
  MailOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { useBranding } from '../hooks/useBranding';
import { AUTH_USER_CHANGED_EVENT, authService } from '../services/auth';
import {
  canViewProductNavigation,
  canViewUserManagementNavigation,
} from '../modules/navigation/navigationAccessPolicy';
import { canViewGlobalCommunications } from '../modules/notifications/communicationsAccessPolicy';
import { isTesterMode } from '../modules/tester/testerModeAccessPolicy';
import AppHeader from './AppHeader';

const { Content, Sider } = AntLayout;

// Maps a route pathname to the menu i18n key shown as the page title in the
// header. Ordered most-specific-first so the longest matching prefix wins
// (e.g. /app/inventory/reports must be checked before /app/inventory).
// Keep in sync with the app routes (App.tsx) and the sidebar menuItems below
// when adding a section; an unmapped route falls back to the generic appTitle.
const PAGE_TITLE_KEYS: ReadonlyArray<readonly [string, string]> = [
  ['/app/dashboard', 'menu.dashboard'],
  ['/app/tasks', 'menu.tasks'],
  ['/app/orders', 'menu.orders'],
  ['/app/customers', 'menu.customers'],
  ['/app/suppliers', 'menu.suppliers'],
  ['/app/transports', 'menu.transports'],
  ['/app/reports', 'menu.reports'],
  ['/app/documents', 'menu.documents'],
  ['/app/wz', 'menu.wzDocuments'],
  ['/app/invoices', 'menu.invoices'],
  ['/app/pz', 'menu.pzDocuments'],
  ['/app/products', 'menu.products'],
  ['/app/inventory/reports', 'menu.inventoryReports'],
  ['/app/inventory', 'menu.inventory'],
  ['/app/profitability', 'menu.profitability'],
  ['/app/settings/notifications', 'menu.notificationSettings'],
  ['/app/settings', 'menu.settings'],
  ['/app/admin/users', 'menu.userManagement'],
  ['/app/communications', 'menu.communications'],
];

const resolvePageTitleKey = (pathname: string): string | null => {
  const match = PAGE_TITLE_KEYS.find(
    ([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
  return match ? match[1] : null;
};

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const { currentLanguage, changeLanguage, availableLanguages } = useLanguage();
  const { branding } = useBranding();
  const [, setAuthRevision] = useState(0);
  // Collapsible sidebar: when collapsed the Sider shrinks to width 0 (fully
  // hidden) so table pages span the full page width. State lives here (Layout
  // is not remounted on in-app tab navigation) and resets on full refresh.
  // The header toggle button drives this manually; the Sider's `onBreakpoint`
  // also re-syncs it whenever the viewport crosses the `md` (768px) boundary,
  // which intentionally overrides a manual toggle on resize (collapse on
  // entering mobile, expand on returning to desktop).
  const [collapsed, setCollapsed] = useState(false);
  const user = authService.getCurrentUser();

  useEffect(() => {
    const handleAuthUserChanged = () => setAuthRevision((revision) => revision + 1);
    window.addEventListener(AUTH_USER_CHANGED_EVENT, handleAuthUserChanged);
    return () => window.removeEventListener(AUTH_USER_CHANGED_EVENT, handleAuthUserChanged);
  }, []);

  const handleLogout = () => {
    authService.logout();
  };

  const isTester = isTesterMode(user);
  const canViewReports = authService.canViewFinancialReports();
  const canViewWarehouse = authService.canViewWarehouseDocuments();
  const canViewProducts = canViewProductNavigation(user);
  const canViewDocumentCenter = authService.canViewDocumentCenter();
  const canViewCommunications = canViewGlobalCommunications(user);
  const canViewUserManagement = canViewUserManagementNavigation(user);
  const isDashboard = location.pathname === '/app/dashboard';
  const pageTitleKey = resolvePageTitleKey(location.pathname);

  // TESTER menu is restricted to Dashboard/Orders/Customers only — no
  // Warehouse, Reports, Invoices, Transports, WZ, Settings, or Notifications
  // (see AGENTS.md "TESTER Role (hidden)"). All other roles keep the full
  // capability-gated menu below.
  const menuItems = isTester ? [
    {
      type: 'group' as const,
      label: t('menu.sections.dailyWork'),
      children: [{
        key: '/app/dashboard',
        icon: <DashboardOutlined />,
        label: t('menu.dashboard'),
      }],
    },
    {
      type: 'group' as const,
      label: t('menu.sections.orders'),
      children: [
        {
          key: '/app/orders',
          icon: <ShoppingCartOutlined />,
          label: t('menu.orders'),
        },
        {
          key: '/app/customers',
          icon: <TeamOutlined />,
          label: t('menu.customers'),
        },
      ],
    },
  ] : [
    {
      type: 'group' as const,
      label: t('menu.sections.dailyWork'),
      children: [
        {
          key: '/app/dashboard',
          icon: <DashboardOutlined />,
          label: t('menu.dashboard'),
        },
        {
          key: '/app/tasks',
          icon: <CheckSquareOutlined />,
          label: t('menu.tasks'),
        },
        ...(canViewCommunications ? [{
          key: '/app/communications',
          icon: <MailOutlined />,
          label: t('menu.communications'),
        }] : []),
      ],
    },
    {
      type: 'group' as const,
      label: t('menu.sections.orders'),
      children: [
        {
          key: '/app/orders',
          icon: <ShoppingCartOutlined />,
          label: t('menu.orders'),
        },
        {
          key: '/app/customers',
          icon: <TeamOutlined />,
          label: t('menu.customers'),
        },
        ...(canViewWarehouse ? [{
          key: '/app/suppliers',
          icon: <ShopOutlined />,
          label: t('menu.suppliers'),
        }] : []),
      ],
    },
    {
      type: 'group' as const,
      label: t('menu.sections.logistics'),
      children: [
        {
          key: '/app/transports',
          icon: <CarOutlined />,
          label: t('menu.transports'),
        },
        ...(canViewWarehouse ? [
          {
            key: '/app/pz',
            icon: <FileAddOutlined />,
            label: t('menu.pzDocuments'),
          },
        ] : []),
        {
          key: '/app/wz',
          icon: <FileTextOutlined />,
          label: t('menu.wzDocuments'),
        },
        ...(canViewWarehouse ? [
          {
            key: '/app/inventory',
            icon: <InboxOutlined />,
            label: t('menu.inventory'),
          },
        ] : []),
      ],
    },
    ...(canViewProducts ? [{
      type: 'group' as const,
      label: t('menu.sections.catalog'),
      children: [{
        key: '/app/products',
        icon: <AppstoreOutlined />,
        label: t('menu.products'),
      }],
    }] : []),
    ...((canViewDocumentCenter || canViewReports) ? [{
      type: 'group' as const,
      label: t('menu.sections.documentsAndReports'),
      children: [
        ...(canViewDocumentCenter ? [{
          key: '/app/documents',
          icon: <FolderOpenOutlined />,
          label: t('menu.documents'),
        }] : []),
        ...(canViewReports ? [
          {
            key: '/app/reports',
            icon: <FileTextOutlined />,
            label: t('menu.reports'),
          },
          {
            key: '/app/profitability',
            icon: <BarChartOutlined />,
            label: t('menu.profitability'),
          },
        ] : []),
      ],
    }] : []),
    ...(canViewUserManagement ? [{
      type: 'group' as const,
      label: t('menu.sections.administration'),
      children: [{
        key: '/app/admin/users',
        icon: <TeamOutlined />,
        label: t('menu.userManagement'),
      }],
    }] : []),
  ];

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: user?.name || 'Profile',
      disabled: true,
    },
    {
      type: 'divider' as const,
    },
    // Settings/notifications are hidden for TESTER — its menu is limited to
    // Dashboard/Orders/Customers and it has no settings surface to manage.
    ...(isTester ? [] : [
      {
        key: 'settings',
        icon: <SettingOutlined />,
        label: t('menu.settings'),
        onClick: () => navigate('/app/settings'),
      },
      {
        key: 'notifications',
        icon: <BellOutlined />,
        label: t('menu.notificationSettings'),
        onClick: () => navigate('/app/settings/notifications'),
      },
      {
        type: 'divider' as const,
      },
    ]),
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('logout'),
      onClick: handleLogout,
    },
  ];

  const appVersion = process.env.REACT_APP_VERSION || 'dev';
  const gitSha = process.env.REACT_APP_GIT_SHA || '';
  const versionLabel = appVersion === 'main' && gitSha
    ? `${appVersion}+${gitSha.slice(0, 7)}`
    : appVersion;

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      <Sider
        width={200}
        theme="dark"
        collapsible
        collapsed={collapsed}
        collapsedWidth={0}
        trigger={null}
        breakpoint="md"
        onBreakpoint={(broken) => setCollapsed(broken)}
      >
        <div style={{ height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div
            aria-label="Azzura"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              minWidth: 0
            }}
          >
            {branding.logoUrl && (
              <img
                src={branding.logoUrl}
                alt=""
                aria-hidden="true"
                style={{ width: 34, height: 34, objectFit: 'contain', flex: '0 0 auto' }}
              />
            )}
            <span
              style={{
                color: '#fff',
                fontSize: 24,
                fontWeight: 700,
                lineHeight: 1,
                letterSpacing: 0
              }}
            >
              Azzura
            </span>
          </div>
        </div>
        <Menu
          className="app-sidebar-menu"
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
        />
        <div style={{ padding: '12px 24px', color: '#555', fontSize: 11 }}>
          v{versionLabel}
        </div>
      </Sider>
      <AntLayout>
        <AppHeader
          collapsed={collapsed}
          pageTitle={t(pageTitleKey ?? 'appTitle')}
          language={currentLanguage}
          languageLabel={t('language')}
          languages={availableLanguages.map(lang => ({
            value: lang.code,
            label: lang.name,
          }))}
          roleLabel={`${t('role')}: ${t(`userRole.${user?.role}`)}`}
          userName={user?.name || ''}
          userMenuItems={userMenuItems}
          toggleSidebarLabel={t(collapsed ? 'expandSidebar' : 'collapseSidebar')}
          onLanguageChange={changeLanguage}
          onToggleSidebar={() => setCollapsed((value) => !value)}
        />
        <Content
          style={isDashboard
            ? { margin: '16px 24px 0', background: '#f5f7fb', padding: 0, minHeight: 280 }
            : { margin: '24px', background: '#fff', padding: 24, minHeight: 280 }}
        >
          <Outlet />
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default Layout;
